// LIBRARY SHELF — the recents + user-galleries UI block that mounts INSIDE the
// paint picker's content, above the house swatches (2026-07-21). Owner's ask:
// "recent colors/gradients/textures, save their own library of swatches in
// their own swatch galleries… the most powerful and user friendly system
// possible for color."
//
// Self-contained: it reads and writes the color library store directly and
// subscribes for cross-shelf liveness, so two mounted pickers stay in step. It
// only asks the host for one thing — an onPick(v) that applies a FillValue,
// the SAME contract the picker's own swatches speak (a swatch click emits the
// FillValue). Recents are WRITTEN by the host at its commit seam (noteRecent);
// the shelf only READS them — clicking a recent just re-applies it.
//
// The optional getPaletteCanvas() callback keeps the shelf decoupled from the
// editor: give it and the color tab grows a "From this poster" row (colors
// already on the artwork) and a "Goes well with" row (a few that sit well
// beside the current color). No canvas is ever mutated.
//
// Words stay educator-plain on screen: "My colors", "New gallery", "Recent",
// "From this poster", "Goes well with" — never "palette"/"harmony"/"extract".
import React, {
  useEffect, useMemo, useReducer, useRef, useState,
} from 'react';
import {
  getRecents, listGalleries, createGallery, renameGallery, deleteGallery,
  saveToGallery, removeFromGallery, moveItem, setGalleryOpen, subscribe,
  kindOfFill, fillHash,
} from './colorLibrary.js';
import { extractPosterPalette, suggestionsFor } from './paletteFromPoster.js';
import { paintKind, paintCss, paintLabel, solidHexFrom } from '../PaintPicker.jsx';
import './colorlib.css';

// Recent DISPLAY cap (owner 2026-07-23: "recent colors needs to be limited to
// 10 colors (1 row)"). The store still keeps 24 (colorLibrary RECENT_CAP) — this
// is purely how many the shelf paints, so the ring stays deep while the row
// stays one line.
const RECENT_VISIBLE = 10;

/** One truthful chip — solid swatch, gradient ramp or texture tile, via the
 * house paintCss reading. Click applies; right-click surfaces its menu. */
function LibChip({ v, active, title, onPick, onContext }) {
  const kind = paintKind(v);
  return (
    <button
      type="button"
      className={`pplib-chip${kind === 'texture' ? ' pplib-tex' : ''}${active ? ' active' : ''}`}
      style={paintCss(v) ?? undefined}
      data-kind={kind}
      title={title ?? paintLabel(v)}
      aria-label={paintLabel(v)}
      onClick={() => onPick(v)}
      onContextMenu={onContext}
    />
  );
}

export default function LibraryShelf({
  kind = 'color',
  currentValue = null,
  onPick,
  density = 'compact',
  getPaletteCanvas,
  // Render split (owner 2026-07-23: galleries fold into an accordion, but Recent
  // and the suggestion rows stay top-level). PaintPicker mounts three instances:
  //   'recent'    — just the Recent ring (capped to ONE row / 10 chips)
  //   'suggest'   — "From this poster" + "Goes well with" (color tab only)
  //   'galleries' — the My-galleries block + its context menu (lives in the
  //                 accordion; the accordion header supplies the heading)
  // 'all' keeps the pre-split behavior for any other caller.
  section = 'all',
}) {
  const [, refresh] = useReducer((n) => n + 1, 0);
  useEffect(() => subscribe(refresh), []);

  const galleries = listGalleries();
  const recents = getRecents(kind);
  const currentHash = currentValue == null ? null : fillHash(currentValue);
  const currentIsColor = kindOfFill(currentValue) === 'color';

  const [activeGalleryId, setActiveGalleryId] = useState(null);
  const activeId = galleries.some((g) => g.id === activeGalleryId)
    ? activeGalleryId
    : (galleries[0]?.id ?? null);

  const [creatingNew, setCreatingNew] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);
  const [menu, setMenu] = useState(null); // { type:'chip'|'gallery', galleryId, hash?, x, y, sub? }

  const newCommitRef = useRef(false);
  const renameCommitRef = useRef(false);

  const showRecent = section === 'recent' || section === 'all';
  const showSuggest = section === 'suggest' || section === 'all';
  const showGalleries = section === 'galleries' || section === 'all';

  // "From this poster" + "Goes well with" — color tab only, cheap-memoized, and
  // only when THIS instance actually renders them (the recent/galleries splits
  // skip the extract/harmony work entirely).
  const posterPalette = useMemo(
    () => (showSuggest && kind === 'color' && typeof getPaletteCanvas === 'function'
      ? extractPosterPalette(getPaletteCanvas())
      : []),
    [showSuggest, kind, currentHash, getPaletteCanvas],
  );
  const suggestions = useMemo(
    () => (showSuggest && kind === 'color' && currentIsColor ? suggestionsFor(solidHexFrom(currentValue)) : []),
    [showSuggest, kind, currentIsColor, currentHash], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const apply = (v) => onPick?.(v);

  // close the popover menu on any outside pointer or Esc
  useEffect(() => {
    if (!menu) return undefined;
    const close = () => setMenu(null);
    const onKey = (e) => { if (e.key === 'Escape') setMenu(null); };
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [menu]);

  const openChipMenu = (e, galleryId, hash) => {
    e.preventDefault();
    e.stopPropagation();
    setMenu({ type: 'chip', galleryId, hash, x: e.clientX, y: e.clientY });
  };
  const openGalleryMenu = (e, galleryId) => {
    e.preventDefault();
    e.stopPropagation();
    setMenu({ type: 'gallery', galleryId, x: e.clientX, y: e.clientY });
  };

  const commitNew = (name) => {
    if (newCommitRef.current) return;
    newCommitRef.current = true;
    const n = String(name ?? '').trim();
    if (n) { const g = createGallery(n); setActiveGalleryId(g.id); }
    setCreatingNew(false);
  };
  const commitRename = (id, name) => {
    if (renameCommitRef.current) return;
    renameCommitRef.current = true;
    const n = String(name ?? '').trim();
    if (n) renameGallery(id, n);
    setRenamingId(null);
  };

  const otherGalleries = menu ? galleries.filter((g) => g.id !== menu.galleryId) : [];
  const menuStyle = menu
    ? {
      left: Math.min(menu.x, (typeof window !== 'undefined' ? window.innerWidth : 9999) - 210),
      top: Math.min(menu.y, (typeof window !== 'undefined' ? window.innerHeight : 9999) - 180),
    }
    : null;

  return (
    <div className="pplib-root" data-density={density} data-shelf-section={section}>
      {showRecent && recents.length > 0 && (
        <div className="pplib-row pplib-row-recent">
          <span className="pplib-minihead">Recent</span>
          <div className="pplib-chips">
            {/* ONE row only — display slice; the ring behind it still holds 24. */}
            {recents.slice(0, RECENT_VISIBLE).map((v, i) => (
              <LibChip key={`r-${fillHash(v)}-${i}`} v={v} active={currentHash === fillHash(v)} onPick={apply} />
            ))}
          </div>
        </div>
      )}

      {showSuggest && posterPalette.length > 0 && (
        <div className="pplib-row">
          <span className="pplib-minihead">From this poster</span>
          <div className="pplib-chips">
            {posterPalette.map(({ hex }) => (
              <LibChip key={`p-${hex}`} v={hex} active={currentHash === fillHash(hex)} onPick={apply} />
            ))}
          </div>
        </div>
      )}

      {showSuggest && suggestions.length > 0 && (
        <div className="pplib-row">
          <span className="pplib-minihead">Goes well with</span>
          <div className="pplib-chips">
            {suggestions.map((hex) => (
              <LibChip key={`s-${hex}`} v={hex} active={currentHash === fillHash(hex)} onPick={apply} />
            ))}
          </div>
        </div>
      )}

      {showGalleries && (
        <div className="pplib-row pplib-row-galleries">
          {/* Heading lives on the accordion header now — no inline minihead. */}
          <div className="pplib-galleries">
          {galleries.map((g) => {
            const isRenaming = renamingId === g.id;
            const isConfirming = confirmingId === g.id;
            const isActive = g.id === activeId;
            if (isConfirming) {
              const n = g.items.length;
              return (
                <div className="pplib-gallery" key={g.id}>
                  <div className="pplib-confirm">
                    <span className="pplib-confirm-text">
                      {`Delete “${g.name}” and its ${n} saved ${n === 1 ? 'color' : 'colors'}?`}
                    </span>
                    <span className="pplib-confirm-actions">
                      <button
                        type="button"
                        className="pplib-btn pplib-btn-danger"
                        onClick={() => { deleteGallery(g.id); setConfirmingId(null); }}
                      >
                        Delete
                      </button>
                      <button type="button" className="pplib-btn" onClick={() => setConfirmingId(null)}>Keep</button>
                    </span>
                  </div>
                </div>
              );
            }
            return (
              <div className="pplib-gallery" key={g.id}>
                <div className="pplib-gal-head" onContextMenu={(e) => openGalleryMenu(e, g.id)}>
                  <button
                    type="button"
                    className="pplib-caret"
                    title={g.open ? 'Collapse' : 'Expand'}
                    aria-label={g.open ? 'Collapse gallery' : 'Expand gallery'}
                    onClick={() => setGalleryOpen(g.id, !g.open)}
                  >
                    {g.open ? '▾' : '▸'}
                  </button>
                  {isRenaming ? (
                    <input
                      className="pplib-input"
                      autoFocus
                      defaultValue={g.name}
                      aria-label="Rename gallery"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') commitRename(g.id, e.currentTarget.value);
                        else if (e.key === 'Escape') { renameCommitRef.current = true; setRenamingId(null); }
                      }}
                      onBlur={(e) => commitRename(g.id, e.currentTarget.value)}
                    />
                  ) : (
                    <button
                      type="button"
                      className="pplib-gal-name"
                      title={g.name}
                      onClick={() => { setActiveGalleryId(g.id); if (!g.open) setGalleryOpen(g.id, true); }}
                    >
                      {g.name}
                    </button>
                  )}
                  <span className="pplib-gal-count">{g.items.length}</span>
                  <button
                    type="button"
                    className="pplib-gal-menu-btn"
                    title="Gallery options"
                    aria-label="Gallery options"
                    onClick={(e) => openGalleryMenu(e, g.id)}
                  >
                    ⋯
                  </button>
                </div>
                {g.open && (
                  <div className="pplib-gal-body" onClick={() => setActiveGalleryId(g.id)}>
                    {g.items.length === 0 && !isActive && (
                      <span className="pplib-gal-empty">No colors saved yet</span>
                    )}
                    {g.items.map((it) => {
                      const h = fillHash(it.v);
                      return (
                        <LibChip
                          key={h}
                          v={it.v}
                          active={currentHash === h}
                          onPick={apply}
                          onContext={(e) => openChipMenu(e, g.id, h)}
                        />
                      );
                    })}
                    {isActive && (
                      <button
                        type="button"
                        className="pplib-save-chip"
                        title="Save the current color here"
                        aria-label="Save the current color here"
                        disabled={currentValue == null}
                        onClick={() => { if (currentValue != null) saveToGallery(g.id, currentValue); }}
                      >
                        ✩
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {creatingNew ? (
          <input
            className="pplib-input"
            autoFocus
            placeholder="Gallery name"
            aria-label="New gallery name"
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitNew(e.currentTarget.value);
              else if (e.key === 'Escape') { newCommitRef.current = true; setCreatingNew(false); }
            }}
            onBlur={(e) => commitNew(e.currentTarget.value)}
          />
        ) : (
          <button
            type="button"
            className="pplib-newgal-btn"
            onClick={() => { newCommitRef.current = false; setCreatingNew(true); }}
          >
            + New gallery
          </button>
        )}
        </div>
      )}

      {showGalleries && menu && (
        <div
          className="pplib-menu"
          style={menuStyle}
          role="menu"
          onPointerDown={(e) => e.stopPropagation()}
        >
          {menu.type === 'gallery' ? (
            <>
              <button
                type="button"
                className="pplib-menu-item"
                role="menuitem"
                onClick={() => { renameCommitRef.current = false; setRenamingId(menu.galleryId); setMenu(null); }}
              >
                Rename
              </button>
              <button
                type="button"
                className="pplib-menu-item pplib-danger"
                role="menuitem"
                onClick={() => { setConfirmingId(menu.galleryId); setMenu(null); }}
              >
                Delete
              </button>
            </>
          ) : menu.sub ? (
            <>
              <span className="pplib-menu-label">Move to</span>
              {otherGalleries.length === 0 && (
                <span className="pplib-menu-note">No other gallery yet</span>
              )}
              {otherGalleries.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  className="pplib-menu-item"
                  role="menuitem"
                  onClick={() => { moveItem(menu.galleryId, g.id, menu.hash); setMenu(null); }}
                >
                  {g.name}
                </button>
              ))}
            </>
          ) : (
            <>
              <button
                type="button"
                className="pplib-menu-item"
                role="menuitem"
                onClick={() => setMenu({ ...menu, sub: true })}
              >
                Move to <span aria-hidden="true">▸</span>
              </button>
              <button
                type="button"
                className="pplib-menu-item pplib-danger"
                role="menuitem"
                onClick={() => { removeFromGallery(menu.galleryId, menu.hash); setMenu(null); }}
              >
                Remove
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
